import api from "@/lib/api";

export const LANGUAGES = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
  { code: "ro", label: "Română" },
  { code: "sq", label: "Shqip" },
  { code: "es", label: "Español" },
  { code: "ar", label: "العربية" },
  { code: "arz", label: "مصري (Egiziano)" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "uk", label: "Українська" },
  { code: "ru", label: "Русский" },
];

const LOADERS = {
  en: () => import("@/i18n/en.json"),
  ro: () => import("@/i18n/ro.json"),
  sq: () => import("@/i18n/sq.json"),
  es: () => import("@/i18n/es.json"),
  ar: () => import("@/i18n/ar.json"),
  arz: () => import("@/i18n/arz.json"),
  de: () => import("@/i18n/de.json"),
  fr: () => import("@/i18n/fr.json"),
  uk: () => import("@/i18n/uk.json"),
  ru: () => import("@/i18n/ru.json"),
};
const ATTRS = ["placeholder", "title", "aria-label"];
const KEY = "iot-lang";
const textOrig = new WeakMap();
const attrOrig = new WeakMap();
let dict = null;
let observer = null;

const UI_SEL = "button,a,label,th,h1,h2,h3,h4,h5,h6,option,legend,[role=tab],[role=menuitem],[data-testid*=status],[data-testid*=badge],[data-testid*=nav],[data-sonner-toast],[class*=uppercase]";

function lookup(s, el) {
  if (!dict || !s) return null;
  const k = s.trim();
  const t = dict[k] || dict[k.replace(/\s+/g, " ")];
  if (t && el && !/\s/.test(k) && !el.closest(UI_SEL)) return null;
  if (!t) return null;
  return s.match(/^\s*/)[0] + t + s.match(/\s*$/)[0];
}

const skip = (el) => !el || /^(SCRIPT|STYLE|CODE)$/.test(el.tagName) || el.closest("[data-no-translate]");

function doText(node) {
  const el = node.parentElement;
  if (skip(el) || el.tagName === "TEXTAREA") return;
  const cur = node.nodeValue;
  const prev = textOrig.get(node);
  const base = prev && prev.t === cur ? prev.o : cur;
  const t = lookup(base, el);
  if (t) {
    textOrig.set(node, { o: base, t });
    if (cur !== t) node.nodeValue = t;
  } else if (prev && prev.t === cur) {
    node.nodeValue = base;
    textOrig.delete(node);
  }
}

function doAttrs(el) {
  if (skip(el)) return;
  const saved = attrOrig.get(el) || {};
  for (const a of ATTRS) {
    const cur = el.getAttribute(a);
    if (cur == null) continue;
    const prev = saved[a];
    const base = prev && prev.t === cur ? prev.o : cur;
    const t = lookup(base);
    if (t) {
      saved[a] = { o: base, t };
      if (cur !== t) el.setAttribute(a, t);
    } else if (prev && prev.t === cur) {
      el.setAttribute(a, base);
      delete saved[a];
    }
  }
  attrOrig.set(el, saved);
}

function walk(root) {
  if (root.nodeType === 3) return doText(root);
  if (root.nodeType !== 1) return;
  doAttrs(root);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = tw.nextNode(); n; n = tw.nextNode()) doText(n);
  root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")).forEach(doAttrs);
}

function startObserver() {
  if (observer) return;
  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "characterData") doText(m.target);
      else if (m.type === "attributes") doAttrs(m.target);
      else m.addedNodes.forEach(walk);
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

export const currentLanguage = () => localStorage.getItem(KEY) || "it";

export function syncLanguageToServer(code = currentLanguage()) {
  if (localStorage.getItem("iot_token")) api.put("/me/language", { language: code }).catch(() => {});
}

export const markLanguagePicked = () => sessionStorage.setItem("iot-lang-picked", "1");

export function adoptUserLanguage(user) {
  const picked = sessionStorage.getItem("iot-lang-picked");
  sessionStorage.removeItem("iot-lang-picked");
  if (picked || !user?.language) syncLanguageToServer();
  else if (user.language !== currentLanguage()) applyLanguage(user.language, false);
}

export async function applyLanguage(code, sync = true) {
  if (sync) syncLanguageToServer(code);
  dict = LOADERS[code] ? (await LOADERS[code]()).default : null;
  localStorage.setItem(KEY, code);
  document.documentElement.lang = code;
  document.documentElement.dir = code === "ar" || code === "arz" ? "rtl" : "ltr";
  window.dispatchEvent(new CustomEvent("iot-lang-change", { detail: code }));
  startObserver();
  walk(document.body);
}
